import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { LibraryNode, PromptArgDef } from "../../lib/types";

/**
 * Sprint 11 — create / update / archive for Prompt nodes. Invalidates the
 * scope-list + dashboard caches (and the parent's children) so the cockpit and
 * /prompts surfaces refresh. The `prompts/list` capability re-fetch on connected
 * hosts is driven server-side via notifications/prompts/list_changed.
 */

function invalidatePromptCaches(qc: ReturnType<typeof useQueryClient>, parentId?: string | null) {
  qc.invalidateQueries({ queryKey: ["prompts-in-scope"] });
  qc.invalidateQueries({ queryKey: queryKeys.allPrompts() });
  qc.invalidateQueries({ queryKey: ["mcp-prompts"] });
  qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
  if (parentId) {
    qc.invalidateQueries({ queryKey: queryKeys.listChildren(parentId) });
    qc.invalidateQueries({ queryKey: queryKeys.libraryNode(parentId) });
  }
}

export function useCreatePrompt() {
  const qc = useQueryClient();
  return useMutation<
    LibraryNode,
    Error,
    { name: string; parent_id?: string; description?: string; body?: string; arguments?: PromptArgDef[] }
  >({
    mutationFn: (params) => mcpClient.createPrompt(params),
    onSuccess: (_node, vars) => invalidatePromptCaches(qc, vars.parent_id),
  });
}

export function useUpdatePrompt() {
  const qc = useQueryClient();
  return useMutation<
    LibraryNode,
    Error,
    { prompt_id: string; name?: string; description?: string; body?: string; arguments?: PromptArgDef[] }
  >({
    mutationFn: (params) => mcpClient.updatePrompt(params),
    onSuccess: (node) => {
      invalidatePromptCaches(qc, node.parent_id);
      qc.invalidateQueries({ queryKey: queryKeys.libraryNode(node.id) });
    },
  });
}

export function useArchivePrompt() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; parent_id?: string | null }>({
    mutationFn: ({ id }) => mcpClient.archiveNodeCall(id),
    onSuccess: (_v, vars) => invalidatePromptCaches(qc, vars.parent_id),
  });
}
